import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { Book, ArrowLeft, AlertCircle } from "lucide-react";
import { useApp } from "@/contexts/AppContext";
import api from "@/utils/axios";
import { toast } from "sonner";

type LoginLockInfo = {
  email: string;
  lockedUntil?: string | null;
  messageKey: string;
};

interface LoginProps {
  navigate: (page: string) => void;
  onLogin: (email: string, password: string) => Promise<void>;
  maintenanceEnabled?: boolean;
  onNavigateToSearchPage?: (target: "categories" | "search") => void;
  loginLockInfo?: LoginLockInfo | null;
  onClearLoginLock?: () => void;
  loginErrorKey?: string | null;
  onClearLoginError?: () => void;
}

export function Login({
  navigate,
  onLogin,
  maintenanceEnabled = false,
  loginLockInfo = null,
  onClearLoginLock,
  loginErrorKey = null,
  onClearLoginError,
}: LoginProps) {
  const { t } = useApp();
  const maxFieldLength = 255;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetSent, setResetSent] = useState(false);
  const [lockRemainingSeconds, setLockRemainingSeconds] = useState<number | null>(null);
  const pageStyle = {
    background:
      "linear-gradient(135deg, color-mix(in srgb, var(--primary) 10%, var(--background)), color-mix(in srgb, var(--surface) 80%, var(--background)))",
  } as const;
  const infoPanelStyle = {
    backgroundColor:
      "color-mix(in srgb, var(--primary) 12%, var(--background))",
    borderColor:
      "color-mix(in srgb, var(--primary) 35%, var(--background))",
  } as const;
  const infoIconStyle = {
    color: "color-mix(in srgb, var(--primary) 85%, var(--foreground))",
  } as const;
  const titleStyle = {
    color: "var(--primary)",
  } as const;
  const descriptionStyle = {
    color: "color-mix(in srgb, var(--foreground) 82%, var(--background))",
  } as const;
  const linkStyle = {
    color: "color-mix(in srgb, var(--primary) 82%, var(--foreground))",
  } as const;
  const primaryTextStyle = {
    color: "var(--foreground)",
  } as const;

  const normalizedEmail = email.trim().toLowerCase();
  const lockedEmail = loginLockInfo?.email?.trim().toLowerCase() ?? "";
  const isLockRelevant = Boolean(loginLockInfo && normalizedEmail && normalizedEmail === lockedEmail);
  const lockedUntilMs = loginLockInfo?.lockedUntil
    ? Date.parse(loginLockInfo.lockedUntil)
    : NaN;
  const hasLockedUntil = Number.isFinite(lockedUntilMs);
  const isTemporaryLock = isLockRelevant && hasLockedUntil;
  const isLockActive =
    isLockRelevant && (lockRemainingSeconds === null || lockRemainingSeconds > 0);

  useEffect(() => {
    if (!isTemporaryLock) {
      setLockRemainingSeconds(null);
      return;
    }
    const updateRemaining = () => {
      const remaining = Math.max(
        0,
        Math.ceil(((lockedUntilMs as number) - Date.now()) / 1000)
      );
      setLockRemainingSeconds(remaining);
      if (remaining <= 0) {
        onClearLoginLock?.();
      }
    };
    updateRemaining();
    const timer = window.setInterval(updateRemaining, 1000);
    return () => window.clearInterval(timer);
  }, [isTemporaryLock, lockedUntilMs, onClearLoginLock]);

  const formatRemaining = (totalSeconds: number | null) => {
    if (totalSeconds === null) return "--:--";
    const safeSeconds = Math.max(0, totalSeconds);
    const hours = Math.floor(safeSeconds / 3600);
    const minutes = Math.floor((safeSeconds % 3600) / 60);
    const seconds = safeSeconds % 60;
    const paddedMinutes = String(minutes).padStart(2, "0");
    const paddedSeconds = String(seconds).padStart(2, "0");
    if (hours > 0) {
      return `${hours}:${paddedMinutes}:${paddedSeconds}`;
    }
    return `${minutes}:${paddedSeconds}`;
  };

  const lockMessageTemplate = t("auth.accountLockedTemporaryMessage");
  const lockMessage = lockMessageTemplate.replace(
    /{{\s*time\s*}}/g,
    formatRemaining(lockRemainingSeconds)
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLockActive) {
      return;
    }
    await onLogin(email, password);
  };

  const handleResetPassword = async (e: React.FormEvent) => {
      e.preventDefault();

      try {
        await api.post("/auth/forgot-password", { email: resetEmail });

        setResetSent(true);
        toast.success("Falls die E-Mail existiert, wurde ein Link gesendet.");

        setTimeout(() => {
          setShowForgotPassword(false);
          setResetSent(false);
          setResetEmail("");
        }, 5000);
      } catch (error) {
        setResetSent(true);
      }
    };

  return (
    <div
      className="flex items-center justify-center px-4 py-6"
      style={{
        ...pageStyle,
        minHeight: "100vh",
        maxHeight: "calc(100vh / var(--ui-scale))",
        overflowY: "auto",
      }}
    >
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="icon-box">
              <Book className="w-8 h-8 icon-primary" />
            </div>
            <div className="text-left">
              <h1 className="text-primary-heading">{t("header.title")}</h1>
              <p className="text-xs text-secondary">{t("header.subtitle")}</p>
            </div>
          </div>
        </div>

        {!showForgotPassword ? (
          <Card>
            <CardHeader>
              <CardTitle className="card-title-text" style={titleStyle}>
                {t("auth.login")}
              </CardTitle>
              <CardDescription className="card-description-text" style={descriptionStyle}>
                {t("auth.loginSubtitle")}
              </CardDescription>
              {maintenanceEnabled && (
                <Alert className="mt-4" style={infoPanelStyle}>
                  <AlertCircle className="h-4 w-4" style={infoIconStyle} />
                  <AlertDescription className="text-sm" style={primaryTextStyle}>
                    {t("auth.maintenanceAdminOnly")}
                  </AlertDescription>
                </Alert>
              )}
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4" autoComplete="on">
                <div className="space-y-2">
                  <Label htmlFor="email">{t("auth.email")}</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    maxLength={maxFieldLength}
                    autoComplete="username"
                    inputMode="email"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    placeholder="ihre.email@beispiel.de"
                    value={email}
                    onChange={(e) => {
                      const nextEmail = e.target.value;
                      setEmail(nextEmail);
                      if (
                        loginLockInfo &&
                        nextEmail.trim().toLowerCase() !== lockedEmail
                      ) {
                        onClearLoginLock?.();
                      }
                      if (loginErrorKey) {
                        onClearLoginError?.();
                      }
                    }}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">{t("auth.password")}</Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    maxLength={maxFieldLength}
                    autoComplete="current-password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (loginErrorKey) {
                        onClearLoginError?.();
                      }
                    }}
                    required
                  />
                </div>

                {loginErrorKey && !isLockRelevant && (
                  <Alert className="mt-4" variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription className="text-sm">
                      {t(loginErrorKey)}
                    </AlertDescription>
                  </Alert>
                )}

                {isLockRelevant && (
                  <Alert className="mt-4" style={infoPanelStyle} variant="destructive">
                    <AlertCircle className="h-4 w-4" style={infoIconStyle} />
                    {isTemporaryLock ? (
                      <>
                        <AlertTitle>{t("auth.accountLockedTemporaryTitle")}</AlertTitle>
                        <AlertDescription className="text-sm" style={primaryTextStyle}>
                          <p>
                            {lockMessage}
                          </p>
                          <p>{t("auth.accountLockedTemporaryHint")}</p>
                          {!showForgotPassword && (
                            <button
                              type="button"
                              onClick={() => {
                                setResetEmail(email);
                                setShowForgotPassword(true);
                              }}
                              className="text-sm underline-offset-4 hover:underline"
                              style={linkStyle}
                            >
                              {t("auth.resetRequestTitle")}
                            </button>
                          )}
                        </AlertDescription>
                      </>
                    ) : (
                      <AlertDescription className="text-sm" style={primaryTextStyle}>
                        {t(loginLockInfo?.messageKey ?? "auth.accountLocked")}
                      </AlertDescription>
                    )}
                  </Alert>
                )}

                {!maintenanceEnabled && (
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm underline-offset-4 hover:underline"
                    style={linkStyle}
                  >
                    {t("auth.forgotPassword")}
                  </button>
                )}

                <Button type="submit" className="w-full" disabled={isLockActive}>
                  {t("auth.loginButton")}
                </Button>

                {!maintenanceEnabled && (
                  <div className="text-center text-sm text-secondary">
                    {t("auth.noAccount")}{" "}
                    <button
                      type="button"
                      onClick={() => navigate("register")}
                      className="underline-offset-4 hover:underline"
                      style={linkStyle}
                    >
                      {t("auth.registerButton")}
                    </button>
                  </div>
                )}
              </form>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="card-title-text" style={titleStyle}>
                {t("auth.resetRequestTitle")}
              </CardTitle>
              <CardDescription className="card-description-text" style={descriptionStyle}>
                {t("auth.resetRequestSubtitle")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {resetSent ? (
                <Alert style={infoPanelStyle}>
                  <AlertCircle className="h-4 w-4" style={infoIconStyle} />
                  <AlertDescription className="text-sm" style={primaryTextStyle}>
                    Ein Link zum Zurücksetzen wurde an{" "}
                    <strong>{resetEmail}</strong> gesendet. Bitte überprüfen Sie
                    Ihr E-Mail-Postfach.
                  </AlertDescription>
                </Alert>
              ) : (
                <form onSubmit={handleResetPassword} className="space-y-4">
                  <div className="space-y-2">
                  <Label htmlFor="reset-email">{t("auth.email")}</Label>
                  <Input
                    id="reset-email"
                    type="email"
                    maxLength={maxFieldLength}
                    placeholder={t("auth.emailPlaceholder")}
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    required
                  />
                </div>

                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowForgotPassword(false)}
                      className="flex-1"
                    >
                      {t("common.cancel")}
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1"
                    >
                      {t("auth.resetRequestSend")}
                    </Button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        )}

        <Button
          variant="ghost"
          onClick={() => navigate("home")}
          className="w-full mt-4 gap-2 text-secondary"
        >
          <ArrowLeft className="w-4 h-4" />
          {t("common.back")}
        </Button>
      </div>
    </div>
  );
}
